export interface Condition {
  fact: string;
  operator: string;
  value: string | number;
}
export interface Config {
  description?: string;
  priority: number;
  effectiveFrom?: string;
  effectiveTo?: string;
  conditions: { all?: Condition[]; any?: Condition[] };
  event: {
    type: string;
    params: {
      ruleName?: string;
      commissionPercentage?: number;
      fixedBonus?: number;
      tiers?: { min: number; max: number; rate: number }[];
      overridePercentage?: number;
      targetAgentId?: string;
      spotCashBonus?: number;
    };
  };
}
export interface Rule {
  id: string;
  name: string;
  is_active: boolean;
  rule_json: Config;
  updatedAt: string;
  validationError?: string;
}
export interface Options {
  agents: { id: string; name: string; role: string }[];
  sections: { id: string; name: string; label: string }[];
  lotTypes: { id: string; name: string }[];
}
export const money = (n: number) =>
  new Intl.NumberFormat("en-PH", { style: "currency", currency: "PHP" }).format(
    n,
  );
export const fields: { [key: string]: string } = {
  agentRole: "Agent role",
  paymentType: "Payment type",
  saleAmount: "Sale amount",
  agentId: "Agent",
  sectionId: "Section",
  lotTypeId: "Lot type",
  stage: "Creation workflow",
};
export const operators: { [key: string]: string } = {
  equal: "is",
  notEqual: "is not",
  greaterThan: "is greater than",
  greaterThanInclusive: "is at least",
  lessThan: "is less than",
  lessThanInclusive: "is at most",
};
export function choices(field: string, o: Options) {
  switch (field) {
    case "agentRole":
      return ["AGENT", "MANAGER", "BROKER", "ADMIN"].map((id) => ({
        id,
        name: id.charAt(0) + id.slice(1).toLowerCase(),
      }));
    case "paymentType":
      return [
        { id: "CASH", name: "Cash" },
        { id: "INSTALLMENT", name: "Installment" },
      ];
    case "agentId":
      return o.agents;
    case "sectionId":
      return o.sections.map((s) => ({ id: s.id, name: s.label || s.name }));
    case "lotTypeId":
      return o.lotTypes;
    case "stage":
      return [
        { id: "INQUIRY", name: "Sales pipeline" },
        { id: "RESERVATION", name: "Map reservation" },
      ];
    default:
      return [];
  }
}
export const conditionText = (c: Condition, o: Options) =>
  `${fields[c.fact] || c.fact} ${operators[c.operator] || c.operator} ${c.fact === "saleAmount" ? money(Number(c.value)) : choices(c.fact, o).find((v) => v.id === c.value)?.name || c.value}`;
export function formula(c: Config) {
  const p = c.event?.params || {};
  return [
    p.overridePercentage !== undefined
      ? `${p.overridePercentage}% agent override`
      : p.tiers
        ? `${p.tiers.length} whole-sale tiers`
        : p.commissionPercentage !== undefined
          ? `${p.commissionPercentage}% of sale`
          : null,
    p.fixedBonus !== undefined ? `${money(p.fixedBonus)} fixed` : null,
    p.spotCashBonus !== undefined
      ? `${money(p.spotCashBonus)} cash bonus`
      : null,
  ]
    .filter(Boolean)
    .join(" + ");
}
export const newConfig = (type = "percentage"): Config => ({
  description: "",
  priority: 1,
  conditions: { all: [] },
  event: {
    type: "commission",
    params:
      type === "fixed"
        ? { fixedBonus: 1000 }
        : type === "tiered"
          ? { tiers: [{ min: 0, max: 0, rate: 5 }] }
          : type === "override"
            ? { overridePercentage: 5, targetAgentId: "" }
            : { commissionPercentage: 5 },
  },
});
